import type {
  IBattingScoreCardItem,
  IBowlingScoreCardItem,
  ICommentaryBall,
  IMatchCommentary,
  IMatchDetails,
  InningScorecard,
} from "../../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";

/* ------------------------------------------------------------------ */
/* generic helpers                                                     */
/* ------------------------------------------------------------------ */

const isNullLike = (s?: string | null) => {
  const t = (s ?? "").trim().toLowerCase();
  return !t || t === "null" || t === "undefined";
};

export const personName = (first?: string | null, last?: string | null) =>
  [first, last].filter((p): p is string => !isNullLike(p)).map((p) => p.trim()).join(" ");

export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

export const oversText = (balls: number) => `${Math.floor(balls / 6)}${balls % 6 ? "." + (balls % 6) : ""}`;

const COLORS = ["#2B4C8C", "#3D6B4F", "#5B4A8F", "#A35A1F", "#7E3A55", "#2C6A80", "#8A6D24", "#4A5262", "#6B3D8C", "#8C2B2B"];
const colorIndex = (s?: string | null) => [...(s ?? "")].reduce((a, c) => a + c.charCodeAt(0), 0) % COLORS.length;

export type TeamInfo = {
  id: string;
  name: string;
  code: string;
  color: string;
  soft: string;
  image?: string | null;
};

const codeFor = (name?: string | null, code?: string | null) =>
  code && code.trim() ? code.trim().slice(0, 3).toUpperCase() : (name ?? "?").split(/\s+/).filter(Boolean).map((w) => w[0]).join("").slice(0, 3).toUpperCase();

export const normName = (s?: string | null) => (s ?? "").trim().toLowerCase();

export const buildTeams = (m: IMatchDetails): [TeamInfo, TeamInfo] => {
  let i1 = colorIndex(m.teamOne?.teamName);
  const i2 = colorIndex(m.teamTwo?.teamName);
  if (i1 === i2) i1 = (i1 + 1) % COLORS.length;
  const mk = (t: IMatchDetails["teamOne"], idx: number): TeamInfo => ({
    id: t?.teamId ?? "",
    name: t?.teamName ?? "-",
    code: codeFor(t?.teamName, t?.teamCode),
    color: COLORS[idx],
    soft: `${COLORS[idx]}1F`,
    image: t?.imageUrl,
  });
  return [mk(m.teamOne, i1), mk(m.teamTwo, i2)];
};

/* ------------------------------------------------------------------ */
/* innings                                                             */
/* ------------------------------------------------------------------ */

export type Inn = {
  idx: number;
  id: string;
  sc: InningScorecard;
  team: TeamInfo;
  opp: TeamInfo;
  runs: number;
  wkts: number;
  legal: number;
  maxOvers: number;
  batting: IBattingScoreCardItem[];
  bowling: IBowlingScoreCardItem[];
  extras: { b: number; lb: number; wd: number; nb: number; pen: number; t: number };
};

export const isBatted = (b: IBattingScoreCardItem) => (b.balls ?? 0) > 0 || (b.run ?? 0) > 0 || (!!b.outString && b.outString.trim() !== "" && b.outString.trim() !== "-");
export const isNotOut = (b: IBattingScoreCardItem) => /not\s*out/i.test(b.outString ?? "") || b.status === "STRIKER" || b.status === "NON_STRIKER";

export const bowlerBalls = (b: IBowlingScoreCardItem) => Number(b.over ?? 0) * 6 + Number(b.ball ?? 0);
export const bowlerOvers = (b: IBowlingScoreCardItem) => `${Number(b.over ?? 0)}.${Number(b.ball ?? 0)}`;

export const buildInnings = (m: IMatchDetails, scores: InningScorecard[] | undefined, teams: [TeamInfo, TeamInfo]): Inn[] => {
  const list = scores?.length ? scores : m.inningScorecardList ?? [];
  const byName = (name?: string | null) => teams.find((t) => normName(t.name) === normName(name));
  return list.map((sc, idx) => {
    const alt = (m.inningScorecardList ?? []).find((x) => x.inningId === sc.inningId);
    const batting = sc.battingScoreCardResponseList?.length ? sc.battingScoreCardResponseList : alt?.battingScoreCardResponseList ?? [];
    const bowling = sc.bowlingScoreCardResponses?.length ? sc.bowlingScoreCardResponses : alt?.bowlingScoreCardResponses ?? [];
    const team = byName(sc.battingTeamName) ?? teams[idx % 2];
    const opp = byName(sc.bowlingTeamName) ?? (team === teams[0] ? teams[1] : teams[0]);
    const wd = sc.wide ?? alt?.wide ?? 0;
    const nb = sc.noBall ?? alt?.noBall ?? 0;
    const b = sc.bye ?? alt?.bye ?? 0;
    const lb = sc.legBye ?? alt?.legBye ?? 0;
    const pen = sc.penalty ?? alt?.penalty ?? 0;
    return {
      idx,
      id: sc.inningId,
      sc,
      team,
      opp,
      runs: sc.runs ?? 0,
      wkts: sc.wicket ?? 0,
      legal: Number(sc.over ?? 0) * 6 + Number(sc.ball ?? 0),
      maxOvers: sc.maxOvers ?? 0,
      batting: batting.filter(isBatted),
      bowling,
      extras: { b, lb, wd, nb, pen, t: b + lb + wd + nb + pen },
    };
  });
};

/** Test / multi-day: a chase target and over-limit based charts do not apply, whatever number of innings has been played so far. */
export const isMultiDay = (m: IMatchDetails, inns: Inn[]) =>
  inns.length > 2 || /TEST/i.test(m.matchType ?? "") || (m.scheduledDays ?? 1) > 1 || inns.some((i) => i.maxOvers > 100);

/**
 * Match phases for a format.
 * - T20: Powerplay (overs 1-6), middle (7-15), death (16-20).
 * - 50 overs: P1 (1-10), P2 (11-40), P3 (41-50).
 * - Test / multi-day: none; there is no powerplay.
 * - Other limited-overs lengths (T10, 30, 40 ...): neutral thirds, not called powerplay.
 * `named` = real format phases (used for chart bands, key moments and team comparison); neutral thirds only feed the Analysis table.
 * `moment` = a phase whose end is worth a key-moment line ("Powerplay ends at 52/2").
 */
export type Phase = { key: string; label: string; short: string; from: number; to: number; named: boolean; moment: boolean };
export const phasesFor = (matchType: string | null | undefined, maxOvers: number, multiDay: boolean): Phase[] => {
  if (multiDay || !maxOvers || maxOvers <= 0 || maxOvers > 100) return [];
  const t = (matchType ?? "").toUpperCase();
  if (maxOvers === 20 || (t === "T20" && maxOvers >= 6)) {
    const death = Math.max(maxOvers - 5, 7);
    return [
      { key: "pp", label: "Powerplay (overs 1-6)", short: "Powerplay", from: 0, to: 6, named: true, moment: true },
      { key: "mid", label: `Middle (overs 7-${death})`, short: "Middle", from: 6, to: death, named: true, moment: false },
      { key: "death", label: `Death (overs ${death + 1}-${maxOvers})`, short: "Death", from: death, to: maxOvers, named: true, moment: false },
    ].filter((p) => p.to > p.from);
  }
  if (maxOvers === 50) {
    return [
      { key: "p1", label: "P1 (overs 1-10)", short: "P1", from: 0, to: 10, named: true, moment: true },
      { key: "p2", label: "P2 (overs 11-40)", short: "P2", from: 10, to: 40, named: true, moment: true },
      { key: "p3", label: "P3 (overs 41-50)", short: "P3", from: 40, to: 50, named: true, moment: false },
    ];
  }
  const t1 = Math.round(maxOvers / 3);
  const t2 = Math.round((2 * maxOvers) / 3);
  return [
    { key: "a", label: `Overs 1 to ${t1}`, short: `1-${t1}`, from: 0, to: t1, named: false, moment: false },
    { key: "b", label: `Overs ${t1 + 1} to ${t2}`, short: `${t1 + 1}-${t2}`, from: t1, to: t2, named: false, moment: false },
    { key: "c", label: `Overs ${t2 + 1} to ${maxOvers}`, short: `${t2 + 1}-${maxOvers}`, from: t2, to: maxOvers, named: false, moment: false },
  ].filter((p) => p.to > p.from);
};

/** "Powerplay ends at 52/2"-style moments at the end of each phase that has one. */
const phaseMoments = (overs: OverVm[], phases: Phase[]): MomentVm[] =>
  phases
    .filter((p) => p.named && p.moment)
    .flatMap((p) => {
      const o = overs.find((x) => x.n === p.to);
      return o ? [{ at: `${p.to}.0`, kind: "m" as const, ord: p.to * 6 + 0.5, bold: `${p.short} ends at ${o.cumRuns}/${o.cumWkts}`, text: "." }] : [];
    });

/** An innings that actually started (multi-day matches list the next innings as 0/0 before a ball is bowled). */
export const played = (i: Inn) => i.legal > 0 || i.runs > 0;

export const runRate = (runs: number, legal: number) => (legal > 0 ? runs / (legal / 6) : 0);

export const winnerOf = (equation: string, teams: [TeamInfo, TeamInfo]): TeamInfo | null => {
  const mt = equation.match(/^(.*?)\s+won\b/i);
  if (!mt) return null;
  return teams.find((t) => normName(t.name) === normName(mt[1])) ?? null;
};

/* ------------------------------------------------------------------ */
/* commentary                                                          */
/* ------------------------------------------------------------------ */

const isWideOrNb = (b: ICommentaryBall) => /WIDE|NO_?BALL/i.test(b.extra?.type ?? "");
export const isLegalBall = (b: ICommentaryBall) => !isWideOrNb(b);
export const extraLabel = (b: ICommentaryBall): string | null => {
  const t = (b.extra?.type ?? "").toUpperCase();
  if (!t || t === "NONE") return null;
  if (/WIDE/.test(t)) return "wd";
  if (/NO_?BALL/.test(t)) return "nb";
  if (/LEG/.test(t)) return "lb";
  if (/BYE/.test(t)) return "b";
  return null;
};
export const isWicketBall = (b: ICommentaryBall) => b.wicket?.wicket_id != null;

export const sortBalls = (balls: ICommentaryBall[]) =>
  [...balls].sort((a, b) => {
    const tA = Number(a.createdAt);
    const tB = Number(b.createdAt);
    if (!Number.isNaN(tA) && !Number.isNaN(tB) && tA !== tB) return tA - tB;
    const nA = Number(a.ball_number);
    const nB = Number(b.ball_number);
    if (!Number.isNaN(nA) && !Number.isNaN(nB) && nA !== nB) return nA - nB;
    return (a.ball_id ?? 0) - (b.ball_id ?? 0);
  });

export type OverVm = {
  /** 1-based over number */
  n: number;
  /** over number as sent by the API (used for the "0.1" style ball labels) */
  api: number;
  raw: IMatchCommentary;
  balls: ICommentaryBall[];
  runs: number;
  wkts: number;
  cumRuns: number;
  cumWkts: number;
};

export type FowVm = { score: number; w: number; who: string; how: string; at: string; x: number; playerId?: string };
export type PartnershipVm = { a: string; b: string; runs: number; balls: number; unbeaten: boolean; fours?: number; sixes?: number };
export type MomentVm = { at: string; kind: "w" | "m" | "end"; text: string; bold?: string; ord: number };
export type CommVm = {
  overs: OverVm[];
  fow: FowVm[];
  partnerships: PartnershipVm[];
  moments: MomentVm[];
  wicketPoints: { x: number; y: number; label: string }[];
};

const nameOfSquad = (p?: { playerFirstName?: string | null; playerLastName?: string | null } | null) => personName(p?.playerFirstName, p?.playerLastName);

export const ballLabel = (o: OverVm, b: ICommentaryBall) => `${b.overNumber ?? o.api}.${b.ball_number ?? 0}`;

const dismissedName = (b: ICommentaryBall, inn: Inn) => {
  const key = (b.wicket?.batsman ?? "").trim();
  if (key) {
    const hit = inn.batting.find((x) => x.playerId === key) ?? inn.batting.find((x) => normName(personName(x.playerFirstName, x.playerLastName)) === normName(key));
    if (hit) return { name: personName(hit.playerFirstName, hit.playerLastName), id: hit.playerId };
  }
  return { name: nameOfSquad(b.batsman), id: b.batsman?.playerId };
};

export const buildComm = (raw: IMatchCommentary[] | undefined, inn: Inn, phases: Phase[] = []): CommVm | null => {
  if (!raw?.length) return null;
  const sorted = [...raw].sort((a, b) => a.overNumber - b.overNumber);
  const zeroBased = sorted.some((o) => o.overNumber === 0 || (o.ballsResponseList ?? []).some((b) => b.overNumber === 0)) || !sorted.some((o) => o.overNumber === 1);
  const overs: OverVm[] = sorted.map((o) => ({
    n: zeroBased ? o.overNumber + 1 : o.overNumber,
    api: o.overNumber,
    raw: o,
    balls: sortBalls(o.ballsResponseList ?? []),
    runs: o.overRuns ?? 0,
    wkts: o.overWickets ?? 0,
    cumRuns: o.totalRuns ?? 0,
    cumWkts: o.totalWickets ?? 0,
  }));

  const fow: FowVm[] = [];
  const wicketPoints: CommVm["wicketPoints"] = [];
  const partnerships: PartnershipVm[] = [];
  const moments: MomentVm[] = [];
  const bat: Record<string, { runs: number; balls: number; name: string; f50: boolean; f100: boolean }> = {};
  let legalIdx = 0;
  let lastWicketIdx = 0;
  let lastWicketScore = 0;
  let pFours = 0;
  let pSixes = 0;
  let wNo = 0;
  let lastBall: ICommentaryBall | null = null;
  let lastOver: OverVm | null = null;

  overs.forEach((o) => {
    o.balls.forEach((b) => {
      const legal = isLegalBall(b);
      if (legal) legalIdx += 1;
      lastBall = b;
      lastOver = o;
      if (!extraLabel(b) || extraLabel(b) === "nb") { if (b.runs_scored === 4) pFours += 1; else if (b.runs_scored === 6) pSixes += 1; }
      const pid = b.batsman?.playerId;
      if (pid) {
        const st = (bat[pid] = bat[pid] ?? { runs: 0, balls: 0, name: nameOfSquad(b.batsman), f50: false, f100: false });
        const lbl = extraLabel(b);
        if (!lbl || lbl === "nb") st.runs += b.runs_scored ?? 0;
        if (legal) st.balls += 1;
        const at = ballLabel(o, b);
        if (!st.f50 && st.runs >= 50) {
          st.f50 = true;
          if (st.runs < 100) moments.push({ at, kind: "m", ord: legalIdx, bold: `${st.name} reaches 50`, text: ` off ${st.balls} balls.` });
        }
        if (!st.f100 && st.runs >= 100) {
          st.f100 = true;
          moments.push({ at, kind: "m", ord: legalIdx, bold: `${st.name} reaches 100`, text: ` off ${st.balls} balls.` });
        }
      }
      if (isWicketBall(b)) {
        wNo += 1;
        const d = dismissedName(b, inn);
        const score = typeof b.currentScore === "number" ? b.currentScore : o.cumRuns;
        const at = ballLabel(o, b);
        const how = (b.wicket?.type ?? "").replace(/_/g, " ").toLowerCase();
        const x = (o.n - 1) + (b.ball_number ?? 0) / 6;
        fow.push({ score, w: wNo, who: d.name, how, at, x, playerId: d.id });
        wicketPoints.push({ x, y: score, label: `${d.name} out at ${score}/${wNo} (${at} ov)` });
        const striker = nameOfSquad(b.batsman);
        const other = nameOfSquad(b.nonStriker);
        partnerships.push({ a: striker || d.name, b: other, runs: score - lastWicketScore, balls: legalIdx - lastWicketIdx, unbeaten: false, fours: pFours, sixes: pSixes });
        pFours = 0;
        pSixes = 0;
        lastWicketIdx = legalIdx;
        lastWicketScore = score;
        moments.push({ at, kind: "w", ord: legalIdx, bold: `${d.name}${how ? ` ${how}` : ""}`, text: ` ${score}/${wNo}.` });
      }
    });
  });

  const lb = lastBall as ICommentaryBall | null;
  const lo = lastOver as OverVm | null;
  if (lb && lo && inn.wkts < 10 && legalIdx > lastWicketIdx) {
    partnerships.push({ a: nameOfSquad(lb.batsman), b: nameOfSquad(lb.nonStriker), runs: Math.max(0, inn.runs - lastWicketScore), balls: Math.max(0, legalIdx - lastWicketIdx), fours: pFours, sixes: pSixes, unbeaten: true });
  }

  const sortedMoments = moments;
  // phase-end markers (T20 powerplay, 50-over P1/P2) + innings end marker
  sortedMoments.push(...phaseMoments(overs, phases));
  sortedMoments.sort((a, b) => a.ord - b.ord);
  sortedMoments.push({ at: oversText(inn.legal), kind: "end", ord: Number.MAX_SAFE_INTEGER, bold: `${inn.team.name} finish on ${inn.runs}/${inn.wkts}`, text: ` after ${oversText(inn.legal)} overs.` });

  return { overs, fow, partnerships, moments: sortedMoments, wicketPoints };
};

/* ------------------------------------------------------------------ */
/* run progression from the bulk /match/{id}/progression endpoint        */
/* ------------------------------------------------------------------ */

export type ProgressionInning = {
  inningId: string;
  legalBalls: number;
  overs: { over: number; runs: number; wickets: number; extras: number; cumRuns: number; cumWickets: number; legalBalls?: number }[];
  wicketEvents: { wicketNo: number; score: number; at: string; x: number; batsmanId?: string | null; batsman?: string | null; bowler?: string | null; howOut?: string | null; legalBallNo: number }[];
  milestones: { runs: number; playerId?: string | null; player?: string | null; balls: number; at: string; legalBallNo: number }[];
};
export type MatchProgression = { matchId: string; innings: ProgressionInning[] };

type ApiPartnership = {
  batsmanOne?: { playerFirstName?: string | null; playerLastName?: string | null } | null;
  batsmanTwo?: { playerFirstName?: string | null; playerLastName?: string | null } | null;
  totalRuns?: number | null; totalBalls?: number | null;
  batsmanOneFours?: number | null; batsmanTwoFours?: number | null; batsmanOneSixes?: number | null; batsmanTwoSixes?: number | null;
};

/**
 * Builds the same CommVm the commentary-based path produces (minus ball-by-ball), but from the bulk progression
 * response plus the scorecard's own partnerships, so the Summary and Scorecard tabs need no commentary paging.
 */
export const buildCommFromProgression = (p: ProgressionInning | undefined, inn: Inn, phases: Phase[] = []): CommVm | null => {
  if (!p) return null;
  const overs: OverVm[] = p.overs.map((o) => ({
    n: o.over, api: o.over, raw: {} as IMatchCommentary, balls: [], runs: o.runs, wkts: o.wickets, cumRuns: o.cumRuns, cumWkts: o.cumWickets,
  }));
  const fow: FowVm[] = p.wicketEvents.map((w) => ({
    score: w.score, w: w.wicketNo, who: w.batsman ?? "", how: (w.howOut ?? "").replace(/_/g, " ").toLowerCase(), at: w.at, x: w.x, playerId: w.batsmanId ?? undefined,
  }));
  const wicketPoints = p.wicketEvents.map((w) => ({ x: w.x, y: w.score, label: `${w.batsman ?? ""} out at ${w.score}/${w.wicketNo} (${w.at} ov)` }));

  const raw = ((inn.sc as unknown as { partnershipAnalysisResponse?: ApiPartnership[] }).partnershipAnalysisResponse ?? []);
  const partnerships: PartnershipVm[] = raw.map((q, i) => ({
    a: personName(q.batsmanOne?.playerFirstName, q.batsmanOne?.playerLastName),
    b: personName(q.batsmanTwo?.playerFirstName, q.batsmanTwo?.playerLastName),
    runs: q.totalRuns ?? 0,
    balls: q.totalBalls ?? 0,
    fours: (q.batsmanOneFours ?? 0) + (q.batsmanTwoFours ?? 0),
    sixes: (q.batsmanOneSixes ?? 0) + (q.batsmanTwoSixes ?? 0),
    unbeaten: inn.wkts < 10 && i === raw.length - 1,
  }));

  const moments: MomentVm[] = [];
  p.wicketEvents.forEach((w) => moments.push({ at: w.at, kind: "w", ord: w.legalBallNo, bold: `${w.batsman ?? ""}${w.howOut ? ` ${w.howOut.replace(/_/g, " ").toLowerCase()}` : ""}`, text: ` ${w.score}/${w.wicketNo}.` }));
  p.milestones.forEach((m) => moments.push({ at: m.at, kind: "m", ord: m.legalBallNo, bold: `${m.player ?? ""} reaches ${m.runs}`, text: ` off ${m.balls} balls.` }));
  moments.push(...phaseMoments(overs, phases));
  moments.sort((a, b) => a.ord - b.ord);
  moments.push({ at: oversText(inn.legal), kind: "end", ord: Number.MAX_SAFE_INTEGER, bold: `${inn.team.name} finish on ${inn.runs}/${inn.wkts}`, text: ` after ${oversText(inn.legal)} overs.` });

  return { overs, fow, partnerships, moments, wicketPoints };
};

/* ------------------------------------------------------------------ */
/* worm / phases                                                       */
/* ------------------------------------------------------------------ */

export type WormPoint = { x: number; y: number };
export const wormPoints = (comm: CommVm, inn: Inn): WormPoint[] => {
  const pts: WormPoint[] = [{ x: 0, y: 0 }];
  comm.overs.forEach((o, i) => {
    const isLast = i === comm.overs.length - 1;
    const x = isLast && inn.legal / 6 > o.n - 1 && inn.legal / 6 < o.n ? inn.legal / 6 : o.n;
    pts.push({ x, y: o.cumRuns });
  });
  return pts;
};

export const cumAfter = (comm: CommVm | null, over: number): { runs: number; wkts: number } | null => {
  if (!comm) return null;
  const o = comm.overs.find((x) => x.n === over);
  return o ? { runs: o.cumRuns, wkts: o.cumWkts } : null;
};

export type PhaseCell = { runs: number; wkts: number; rr: number; balls: number } | null;
export const phaseCell = (comm: CommVm | null, inn: Inn, a: number, b: number): PhaseCell => {
  if (!comm) return null;
  const balls = Math.min(inn.legal, b * 6) - a * 6;
  if (balls <= 0) return null;
  const upto = (n: number) => {
    if (n <= 0) return { runs: 0, wkts: 0 };
    const o = [...comm.overs].reverse().find((x) => x.n <= n);
    return o ? { runs: o.cumRuns, wkts: o.cumWkts } : { runs: 0, wkts: 0 };
  };
  const s = upto(a);
  const e = upto(b);
  const runs = e.runs - s.runs;
  return { runs, wkts: e.wkts - s.wkts, rr: runs / (balls / 6), balls };
};

export const bestBatter = (inns: Inn[]) => {
  let best: { name: string; id: string; runs: number; balls: number; inn: Inn } | null = null;
  inns.forEach((i) =>
    i.batting.forEach((b) => {
      if (!best || (b.run ?? 0) > best.runs) best = { name: personName(b.playerFirstName, b.playerLastName), id: b.playerId, runs: b.run ?? 0, balls: b.balls ?? 0, inn: i };
    }),
  );
  return best as { name: string; id: string; runs: number; balls: number; inn: Inn } | null;
};

export const sortBowlers = (list: IBowlingScoreCardItem[]) => [...list].sort((a, b) => (b.wickets ?? 0) - (a.wickets ?? 0) || (a.runs ?? 0) - (b.runs ?? 0));

export const bestBowler = (inns: Inn[]) => {
  let best: { b: IBowlingScoreCardItem; inn: Inn } | null = null;
  inns.forEach((i) =>
    i.bowling.forEach((b) => {
      if (!best || (b.wickets ?? 0) > (best.b.wickets ?? 0) || ((b.wickets ?? 0) === (best.b.wickets ?? 0) && (b.runs ?? 0) < (best.b.runs ?? 0))) best = { b, inn: i };
    }),
  );
  return best as { b: IBowlingScoreCardItem; inn: Inn } | null;
};

export const strikeRate = (runs: number, balls: number) => (balls > 0 ? ((runs * 100) / balls).toFixed(1) : "0.0");
